/**
 * Point d'accès unique à l'API Sahel Fuel Control.
 *
 * Tout appel réseau passe par ici : URL de base, en-tête `Authorization`,
 * et traduction des statuts HTTP en erreurs typées. Le
 * reste de l'application ne connaît ni `fetch`, ni les codes de statut.
 */
import type { ValidationErrors } from './types'

const BASE_URL = (import.meta.env.VITE_API_BASE_URL ?? '/api').replace(/\/$/, '')

/** Session : le jeton, conservé en `sessionStorage`, jamais dans le bundle. */
const TOKEN_KEY = 'sfc.token'

export const session = {
  get token(): string | null {
    try {
      return sessionStorage.getItem(TOKEN_KEY)
    } catch {
      return null
    }
  },
  open(token: string): void {
    try {
      sessionStorage.setItem(TOKEN_KEY, token)
    } catch {
      /* navigation privée : la session ne survivra pas au rechargement */
    }
  },
  close(): void {
    try {
      sessionStorage.removeItem(TOKEN_KEY)
    } catch {
      /* ignore */
    }
  },
}

/* ── Erreurs typées ──────────────────────────────────────────── */

export class ApiError extends Error {
  readonly status: number
  readonly payload: unknown

  constructor(status: number, payload: unknown, message: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.payload = payload
  }
}

/** 422 — les messages sont rédigés par le serveur, en français, champ par champ. */
export class ValidationError extends ApiError {
  readonly errors: Record<string, string[]>

  constructor(payload: ValidationErrors) {
    super(422, payload, payload.message)
    this.name = 'ValidationError'
    this.errors = payload.errors ?? {}
  }

  /** Premier message d'un champ, ou `undefined`. */
  field(name: string): string | undefined {
    return this.errors[name]?.[0]
  }
}

/** 403 porteur d'une clé `module` — la fonction n'est pas souscrite. */
export class ModuleDisabledError extends ApiError {
  readonly module: string

  constructor(payload: { message: string; module: string }) {
    super(403, payload, payload.message)
    this.name = 'ModuleDisabledError'
    this.module = payload.module
  }
}

/** 401 — session expirée ou jeton révoqué. */
export class UnauthenticatedError extends ApiError {
  constructor(payload: unknown) {
    super(401, payload, 'Votre session a expiré. Reconnectez-vous.')
    this.name = 'UnauthenticatedError'
  }
}

/* ── Abonnés aux événements de session ───────────────────────── */

type Listener = (
  event: { type: 'unauthenticated' } | { type: 'module-disabled'; module: string },
) => void
const listeners = new Set<Listener>()

export function onSessionEvent(listener: Listener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

/* ── Appel ───────────────────────────────────────────────────── */

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  /** Sans authentification (connexion). */
  anonymous?: boolean
}

function messageOf(payload: unknown, fallback: string): string {
  if (payload && typeof payload === 'object' && 'message' in payload) {
    const m = (payload as { message?: unknown }).message
    if (typeof m === 'string' && m.trim()) return m
  }
  return fallback
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin)
  for (const [k, v] of Object.entries(options.query ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
  }

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (!options.anonymous && session.token) headers.Authorization = `Bearer ${session.token}`

  let response: Response
  try {
    response = await fetch(url.toString(), {
      method: options.method ?? 'GET',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    })
  } catch {
    throw new ApiError(
      0,
      null,
      'API injoignable. Vérifiez votre connexion ou que la pile est démarrée.',
    )
  }

  const payload: unknown = response.status === 204 ? null : await response.json().catch(() => null)

  if (response.ok) return payload as T

  if (response.status === 401) {
    session.close()
    listeners.forEach((l) => l({ type: 'unauthenticated' }))
    throw new UnauthenticatedError(payload)
  }
  if (response.status === 403 && payload && typeof payload === 'object' && 'module' in payload) {
    const p = payload as { message: string; module: string }
    listeners.forEach((l) => l({ type: 'module-disabled', module: p.module }))
    throw new ModuleDisabledError(p)
  }
  if (response.status === 422 && payload && typeof payload === 'object' && 'errors' in payload) {
    throw new ValidationError(payload as ValidationErrors)
  }

  const fallback: Record<number, string> = {
    403: 'Vous ne disposez pas des droits nécessaires.',
    404: 'Ressource introuvable ou hors de votre périmètre.',
    409: 'Cette action est impossible dans l’état actuel de la ressource.',
    429: 'Trop de tentatives. Patientez une minute.',
    500: 'Le serveur a rencontré une erreur.',
  }
  throw new ApiError(
    response.status,
    payload,
    messageOf(payload, fallback[response.status] ?? `L'API a répondu ${response.status}.`),
  )
}

/**
 * Dépose un fichier en multipart.
 *
 * Le `Content-Type` n'est pas fixé : le navigateur doit poser lui-même la
 * frontière du multipart, et l'écrire à la main produirait un corps que le
 * serveur ne sait pas découper.
 */
export async function upload<T>(
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  for (const [k, v] of Object.entries(fields)) form.append(k, v)

  const headers: Record<string, string> = { Accept: 'application/json' }
  if (session.token) headers.Authorization = `Bearer ${session.token}`

  let response: Response
  try {
    response = await fetch(new URL(`${BASE_URL}${path}`, window.location.origin).toString(), {
      method: 'POST',
      headers,
      body: form,
    })
  } catch {
    throw new ApiError(0, null, 'Envoi impossible : vérifiez votre connexion.')
  }

  const payload: unknown = await response.json().catch(() => null)
  if (response.ok) return payload as T

  if (response.status === 401) {
    session.close()
    listeners.forEach((l) => l({ type: 'unauthenticated' }))
    throw new UnauthenticatedError(payload)
  }
  if (response.status === 422 && payload && typeof payload === 'object' && 'errors' in payload) {
    throw new ValidationError(payload as ValidationErrors)
  }
  throw new ApiError(response.status, payload, messageOf(payload, 'Le dépôt a échoué.'))
}

export const api = {
  get: <T>(path: string, query?: RequestOptions['query']) => request<T>(path, { query }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
}

/**
 * Télécharge un fichier servi par l'API.
 *
 * Un `<a href>` n'irait pas : la route exige le jeton, qui vit en mémoire de
 * session et n'est jamais mis dans une URL — une URL se retrouve dans
 * l'historique, dans les journaux du proxy et dans le presse-papier.
 */
export async function download(path: string, query?: RequestOptions['query']): Promise<void> {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin)
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== '') url.searchParams.set(k, String(v))
  }

  const headers: Record<string, string> = { Accept: 'text/csv' }
  if (session.token) headers.Authorization = `Bearer ${session.token}`

  let response: Response
  try {
    response = await fetch(url.toString(), { headers })
  } catch {
    throw new ApiError(0, null, 'API injoignable. Vérifiez votre connexion.')
  }
  if (!response.ok) {
    throw new ApiError(
      response.status,
      null,
      response.status === 403
        ? 'Vous ne disposez pas du droit d’exporter.'
        : `L'API a répondu ${response.status}.`,
    )
  }

  // Le nom du fichier est celui que le serveur a choisi : il porte la
  // période, et le recomposer ici le ferait diverger.
  const disposition = response.headers.get('Content-Disposition') ?? ''
  const named = /filename="?([^";]+)"?/.exec(disposition)
  const blob = await response.blob()
  const href = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = href
  link.download = named?.[1] ?? 'export.csv'
  document.body.append(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)
}

/** UUID v4 pour `client_reference` — généré côté client, conservé jusqu'au succès. */
export function clientReference(): string {
  return crypto.randomUUID()
}
